import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Button,
    Grid,
    Typography,
    TablePagination,
    Stack,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    PictureAsPdf as PictureAsPdfIcon,
    ArrowBack as ArrowBackIcon,
    FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../components/SearchableSelect';
import inventarioInformes, { formatStockInmovilizadoNumero } from '../../services/inventarioInformes';
import type { StockInmovilizadoRow } from '../../services/inventarioInformes';

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}

const hoyIso = () => new Date().toISOString().slice(0, 10);

const hace90DiasIso = () => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
};

const nroUbicacionFiltro = (nroUbicacion: string) =>
    nroUbicacion ? Number(nroUbicacion) : undefined;

const formatUltimaVenta = (v: string | null) => (v ? v : 'Sin ventas');

const StockInmovilizado: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { enqueueSnackbar } = useSnackbar();

    const [filters, setFilters] = useState({
        fechaDesde: hace90DiasIso(),
        fechaHasta: hoyIso(),
        codigoArticulo: '',
        nroUbicacion: '',
    });
    const [applied, setApplied] = useState(filters);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [rows, setRows] = useState<StockInmovilizadoRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalCantidad, setTotalCantidad] = useState(0);
    const [totalValorizado, setTotalValorizado] = useState(0);

    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar los depósitos', { variant: 'error' });
            }
        };
        void loadUbicaciones();
    }, [enqueueSnackbar]);

    const ubicacionOptions = useMemo(
        () =>
            ubicaciones.map((ubicacion) => ({
                value: ubicacion.nroUbicacion,
                label: `${ubicacion.nroUbicacion} - ${ubicacion.nombreUbicacion}`,
            })),
        [ubicaciones],
    );

    const loadData = useCallback(async () => {
        if (!applied.fechaDesde || !applied.fechaHasta) {
            enqueueSnackbar('Indicá el período (desde / hasta)', { variant: 'warning' });
            return;
        }
        try {
            setLoading(true);
            const response = await inventarioInformes.getStockInmovilizado({
                fechaDesde: applied.fechaDesde,
                fechaHasta: applied.fechaHasta,
                codigoArticulo: applied.codigoArticulo || undefined,
                nroUbicacion: nroUbicacionFiltro(applied.nroUbicacion),
                page: page + 1,
                pageSize: rowsPerPage,
            });
            setRows(response.data ?? []);
            setTotalCount(response.totalCount ?? 0);
            setTotalCantidad(response.totalCantidad ?? 0);
            setTotalValorizado(response.totalValorizado ?? 0);
        } catch (error) {
            console.error('Error al cargar stock inmovilizado:', error);
            enqueueSnackbar('Error al cargar los datos', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [applied, page, rowsPerPage, enqueueSnackbar]);

    useEffect(() => {
        void loadData();
    }, [loadData]);

    const handleSearch = () => {
        setPage(0);
        setApplied({ ...filters });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleGeneratePdf = async () => {
        if (!filters.fechaDesde || !filters.fechaHasta) {
            enqueueSnackbar('Indicá el período (desde / hasta)', { variant: 'warning' });
            return;
        }
        try {
            setLoading(true);
            const blob = await inventarioInformes.getStockInmovilizadoPdf({
                fechaDesde: filters.fechaDesde,
                fechaHasta: filters.fechaHasta,
                codigoArticulo: filters.codigoArticulo || undefined,
                nroUbicacion: nroUbicacionFiltro(filters.nroUbicacion),
            });
            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error al generar PDF:', error);
            enqueueSnackbar('Error al generar el PDF', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = async () => {
        if (!filters.fechaDesde || !filters.fechaHasta) {
            enqueueSnackbar('Indicá el período (desde / hasta)', { variant: 'warning' });
            return;
        }
        try {
            setLoading(true);
            const response = await inventarioInformes.getStockInmovilizado({
                fechaDesde: filters.fechaDesde,
                fechaHasta: filters.fechaHasta,
                codigoArticulo: filters.codigoArticulo || undefined,
                nroUbicacion: nroUbicacionFiltro(filters.nroUbicacion),
            });
            const data = response.data ?? [];
            if (data.length === 0) {
                enqueueSnackbar('No hay datos para exportar', { variant: 'info' });
                return;
            }
            const sheetRows: Array<Record<string, string | number>> = data.map((row, index) => ({
                'N°': index + 1,
                Código: row.codigoArticulo,
                Descripción: row.descripcion,
                'U.M.': row.unidadMedida,
                Stock: row.cantidad,
                'Costo Neto': row.costoNeto,
                Valorizado: row.valorizado,
                'Última venta': formatUltimaVenta(row.ultimaVenta),
            }));
            sheetRows.push({
                'N°': '',
                Código: '',
                Descripción: '',
                'U.M.': 'Totales',
                Stock: response.totalCantidad,
                'Costo Neto': '',
                Valorizado: response.totalValorizado,
                'Última venta': '',
            });
            const ws = XLSX.utils.json_to_sheet(sheetRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Stock inmovilizado');
            XLSX.writeFile(
                wb,
                `stock-inmovilizado-${filters.fechaDesde}_${filters.fechaHasta}.xlsx`,
            );
        } catch (error) {
            console.error('Error al exportar Excel:', error);
            enqueueSnackbar('Error al exportar a Excel', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const renderCardCampo = (label: string, valor: React.ReactNode) => (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {label}
            </Typography>
            <Typography variant="body2">{valor}</Typography>
        </Box>
    );

    const nroFila = (index: number) => page * rowsPerPage + index + 1;

    const pagination = (
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
                mt: isMobile ? 2 : 0,
                '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1,
                },
            }}
        />
    );

    const renderCardsMobile = () => {
        if (!rows || rows.length === 0) {
            return (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No hay datos disponibles
                </Typography>
            );
        }

        return (
            <Stack spacing={2}>
                {rows.map((row, index) => (
                    <Paper
                        key={row.codigoArticulo}
                        variant="outlined"
                        sx={{ p: 2, borderRadius: 2 }}
                    >
                        <Grid container spacing={1.5}>
                            <Grid item xs={4}>
                                {renderCardCampo('N°', nroFila(index))}
                            </Grid>
                            <Grid item xs={8}>
                                {renderCardCampo('Código', row.codigoArticulo)}
                            </Grid>
                            <Grid item xs={12}>
                                {renderCardCampo('Descripción', row.descripcion)}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('U.M.', row.unidadMedida)}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('Stock', formatStockInmovilizadoNumero(row.cantidad))}
                            </Grid>
                            <Grid item xs={4}>
                                {renderCardCampo('Costo Neto', formatStockInmovilizadoNumero(row.costoNeto))}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Valorizado', formatStockInmovilizadoNumero(row.valorizado))}
                            </Grid>
                            <Grid item xs={6}>
                                {renderCardCampo('Última venta', formatUltimaVenta(row.ultimaVenta))}
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                            {renderCardCampo('Total stock', formatStockInmovilizadoNumero(totalCantidad))}
                        </Grid>
                        <Grid item xs={6}>
                            {renderCardCampo('Total valorizado', formatStockInmovilizadoNumero(totalValorizado))}
                        </Grid>
                    </Grid>
                </Paper>
            </Stack>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 3 }}
                    >
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/inventario/informes')}
                            sx={{ textTransform: 'none' }}
                        >
                            Informes
                        </Button>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{ fontWeight: 600, color: '#1976d2' }}
                        >
                            Stock inmovilizado
                        </Typography>
                    </Stack>

                    <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Desde"
                                        type="date"
                                        value={filters.fechaDesde}
                                        onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Hasta"
                                        type="date"
                                        value={filters.fechaHasta}
                                        onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="Código Artículo"
                                        value={filters.codigoArticulo}
                                        onChange={(e) => setFilters({ ...filters, codigoArticulo: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <SearchableSelect<number>
                                        label="Depósito"
                                        value={filters.nroUbicacion ? Number(filters.nroUbicacion) : null}
                                        onChange={(val) =>
                                            setFilters({
                                                ...filters,
                                                nroUbicacion: val != null ? String(val) : '',
                                            })
                                        }
                                        options={ubicacionOptions}
                                        allowEmpty
                                        emptyOptionLabel="Todos"
                                    />
                                </Grid>
                            </Grid>
                            <Box
                                mt={2}
                                display="flex"
                                gap={2}
                                flexWrap="wrap"
                                flexDirection={{ xs: 'column', sm: 'row' }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    fullWidth={isMobile}
                                >
                                    Buscar
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => void handleGeneratePdf()}
                                    disabled={loading}
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        px: { xs: 2, sm: 3 },
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    Generar PDF
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={() => void handleExportExcel()}
                                    disabled={loading}
                                    fullWidth={isMobile}
                                    sx={{
                                        borderRadius: 2,
                                        px: { xs: 2, sm: 3 },
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    Exportar Excel
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {isMobile ? (
                        <Box>
                            {renderCardsMobile()}
                            {pagination}
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>N°</TableCell>
                                        <TableCell>Código</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell>U.M.</TableCell>
                                        <TableCell align="right">Stock</TableCell>
                                        <TableCell align="right">Costo Neto</TableCell>
                                        <TableCell align="right">Valorizado</TableCell>
                                        <TableCell>Última venta</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows && rows.length > 0 ? (
                                        rows.map((row, index) => (
                                            <TableRow key={row.codigoArticulo} hover>
                                                <TableCell>{nroFila(index)}</TableCell>
                                                <TableCell>{row.codigoArticulo}</TableCell>
                                                <TableCell>{row.descripcion}</TableCell>
                                                <TableCell>{row.unidadMedida}</TableCell>
                                                <TableCell align="right">
                                                    {formatStockInmovilizadoNumero(row.cantidad)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatStockInmovilizadoNumero(row.costoNeto)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatStockInmovilizadoNumero(row.valorizado)}
                                                </TableCell>
                                                <TableCell>{formatUltimaVenta(row.ultimaVenta)}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No hay datos disponibles
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                {rows.length > 0 && (
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ fontWeight: 600 }}>
                                                Totales
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatStockInmovilizadoNumero(totalCantidad)}
                                            </TableCell>
                                            <TableCell />
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatStockInmovilizadoNumero(totalValorizado)}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                            {pagination}
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default StockInmovilizado;
